import React from "react";
import { Checkbox } from "../ui/checkbox";
import { cn } from "../../lib/utils";

const ACTION_LABELS = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };

/**
 * Checkbox permission matrix driven by the backend permission-catalog.
 * `value` is an array of permission keys; `onChange` receives the next array.
 * When `disabled` (e.g. the admin is a Super Admin), everything shows checked
 * and is read-only since Super Admin implicitly has every permission.
 */
export function PermissionMatrix({ catalog, value, onChange, disabled }) {
  const set = new Set(value || []);
  const has = (k) => disabled || set.has(k);
  const toggle = (k) => {
    if (disabled) return;
    const next = new Set(set);
    next.has(k) ? next.delete(k) : next.add(k);
    onChange(Array.from(next));
  };
  if (!catalog) return null;

  return (
    <div className="space-y-4" data-testid="permission-matrix">
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-background">
            <tr className="text-left text-xs uppercase tracking-wide text-text-muted">
              <th className="px-3 py-2">Module</th>
              {["view", "create", "edit", "delete"].map((a) => (
                <th key={a} className="px-3 py-2 text-center">{ACTION_LABELS[a]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.modules.map((m, i) => (
              <tr key={m.key} className={cn("border-t border-border", i % 2 && "bg-background/50")}>
                <td className="px-3 py-2 font-medium text-text-main">{m.label}</td>
                {["view", "create", "edit", "delete"].map((a) => {
                  const supported = m.actions.includes(a);
                  const key = `${m.key}.${a}`;
                  return (
                    <td key={a} className="px-3 py-2 text-center">
                      {supported ? (
                        <Checkbox
                          checked={has(key)}
                          disabled={disabled}
                          onCheckedChange={() => toggle(key)}
                          data-testid={`perm-${key}`}
                        />
                      ) : (
                        <span className="text-text-muted/40">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          Sensitive actions (granted explicitly)
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {catalog.sensitive.map((s) => (
            <label
              key={s.key}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <Checkbox
                checked={has(s.key)}
                disabled={disabled}
                onCheckedChange={() => toggle(s.key)}
                data-testid={`perm-${s.key}`}
              />
              <span className="text-text-main">{s.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
